import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Phone, MessageSquare, Calendar, User, RefreshCw, Activity } from 'lucide-react'
import {
  getDemoRequests,
  updateDemoRequest,
  getCrmHealth,
  serviceTypeLabel,
  sourceLabel,
  statusColor,
} from '../../lib/services/demoRequests'

const STATUSES = ['new', 'contacted', 'qualified', 'demo_scheduled', 'closed'] as const
const SOURCES = ['cal_com', 'vapi_phone', 'chat_widget', 'manual'] as const
const SERVICE_TYPES = [
  'general',
  'mock_survey',
  'domain_audit',
  'qapi_design',
  'accreditation_prep',
  'compliance_retainer',
  'essentials_bundle',
  'accreditation_ready_bundle',
  'total_compliance_bundle',
] as const

function SourceIcon({ source }: { source: string }) {
  switch (source) {
    case 'cal_com': return <Calendar size={14} />
    case 'vapi_phone': return <Phone size={14} />
    case 'chat_widget': return <MessageSquare size={14} />
    default: return <User size={14} />
  }
}

function HealthDot({ status }: { status: string }) {
  if (status === 'healthy') return <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
  if (status === 'not_configured') return <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
  return <span className="inline-block h-2 w-2 rounded-full bg-rose-500" />
}

export function DemoRequestsListPage() {
  const queryClient = useQueryClient()
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [filterSource, setFilterSource] = useState<string>('')
  const [filterServiceType, setFilterServiceType] = useState<string>('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const requestsQuery = useQuery({
    queryKey: ['demo-requests', filterStatus, filterServiceType],
    queryFn: () => getDemoRequests(filterStatus || undefined, filterServiceType || undefined),
    refetchInterval: 60_000,
  })

  const healthQuery = useQuery({
    queryKey: ['crm-health'],
    queryFn: getCrmHealth,
    staleTime: 60_000,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { status?: string; notes?: string } }) =>
      updateDemoRequest(id, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['demo-requests'] }),
  })

  const requests = (requestsQuery.data ?? []).filter(
    (r) => !filterSource || r.source === filterSource
  )

  // Stats
  const all = requestsQuery.data ?? []
  const bySource = SOURCES.reduce((acc, s) => {
    acc[s] = all.filter((r) => r.source === s).length
    return acc
  }, {} as Record<string, number>)
  const byStatus = STATUSES.reduce((acc, s) => {
    acc[s] = all.filter((r) => r.status === s).length
    return acc
  }, {} as Record<string, number>)

  const health = healthQuery.data

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-20">
      {/* Header */}
      <section className="v11-workspace-header">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">CRM Pipeline</p>
            <h1 className="mt-1 font-brand text-2xl font-bold text-[var(--brand-deep)]">Demo Requests</h1>
          </div>
          <button
            onClick={() => requestsQuery.refetch()}
            className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
          >
            <RefreshCw size={16} className={requestsQuery.isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </section>

      {/* Health Status Bar */}
      {health && (
        <section className="v11-section flex flex-wrap items-center gap-4 px-5 py-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
            <Activity size={12} /> Pipeline Health:
          </div>
          {Object.entries(health).map(([service, status]) => (
            <div key={service} className="flex items-center gap-1.5 text-xs text-slate-600">
              <HealthDot status={status} />
              <span>{service.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </section>
      )}

      {/* Stats Cards */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <div className="v11-kpi-card text-center">
          <p className="text-2xl font-extrabold text-[var(--brand-deep)]">{all.length}</p>
          <p className="mt-1 text-xs font-semibold text-slate-500">Total</p>
        </div>
        {SOURCES.map((s) => (
          <div key={s} className="v11-kpi-card cursor-pointer text-center"
            onClick={() => setFilterSource(filterSource === s ? '' : s)}>
            <p className="text-2xl font-extrabold text-[var(--brand-deep)]">{bySource[s] || 0}</p>
            <p className="mt-1 flex items-center justify-center gap-1 text-xs font-semibold text-slate-500">
              <SourceIcon source={s} /> {sourceLabel(s)}
            </p>
          </div>
        ))}
      </section>

      {/* Filters */}
      <section className="flex flex-wrap gap-3">
        <select
          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace('_', ' ')} ({byStatus[s] || 0})</option>
          ))}
        </select>
        <select
          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700"
          value={filterServiceType}
          onChange={(e) => setFilterServiceType(e.target.value)}
        >
          <option value="">All Service Types</option>
          {SERVICE_TYPES.map((serviceType) => (
            <option key={serviceType} value={serviceType}>{serviceTypeLabel(serviceType)}</option>
          ))}
        </select>
        {filterSource && (
          <button
            onClick={() => setFilterSource('')}
            className="rounded border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600"
          >
            Clear source filter: {sourceLabel(filterSource)} ×
          </button>
        )}
        {filterServiceType && (
          <button
            onClick={() => setFilterServiceType('')}
            className="rounded border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600"
          >
            Clear service filter: {serviceTypeLabel(filterServiceType)} ×
          </button>
        )}
      </section>

      {/* Table */}
      <section className="v11-section overflow-hidden">
        {requestsQuery.isLoading ? (
          <p className="p-4 text-sm text-slate-500">Loading...</p>
        ) : requests.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">No demo requests found.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">CRM</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => {
                const sc = statusColor(req.status)
                const isExpanded = expandedId === req.request_id
                return (
                  <>
                    <tr
                      key={req.request_id}
                      className="cursor-pointer border-b border-slate-100 hover:bg-slate-50/50"
                      onClick={() => setExpandedId(isExpanded ? null : req.request_id)}
                    >
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-slate-600">
                          <SourceIcon source={req.source} /> {sourceLabel(req.source)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-700">{req.name}</td>
                      <td className="px-4 py-3 text-slate-600">{req.email || '—'}</td>
                      <td className="px-4 py-3">
                        {req.tier ? (
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{req.tier}</span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold text-slate-600">
                        {serviceTypeLabel(req.service_type)}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          className={`rounded px-2 py-0.5 text-xs font-bold ${sc.bg} ${sc.text}`}
                          value={req.status}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => updateMutation.mutate({ id: req.request_id, updates: { status: e.target.value } })}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {req.crm_synced_at ? (
                          <span className="text-xs font-semibold text-emerald-600">Synced</span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {new Date(req.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${req.request_id}-detail`} className="border-b border-slate-100 bg-slate-50/30">
                        <td colSpan={8} className="px-6 py-4">
                          <div className="grid grid-cols-2 gap-4 text-xs">
                            {req.service_type && <div><span className="font-bold text-slate-500">Service Type:</span> {serviceTypeLabel(req.service_type)}</div>}
                            {req.phone && <div><span className="font-bold text-slate-500">Phone:</span> {req.phone}</div>}
                            {req.cal_booking_id && <div><span className="font-bold text-slate-500">Cal Booking:</span> {req.cal_booking_id}</div>}
                            {req.vapi_call_id && <div><span className="font-bold text-slate-500">VAPI Call:</span> {req.vapi_call_id}</div>}
                            {req.call_duration_seconds != null && <div><span className="font-bold text-slate-500">Duration:</span> {req.call_duration_seconds}s</div>}
                            {req.call_summary && <div className="col-span-2"><span className="font-bold text-slate-500">Call Summary:</span> {req.call_summary}</div>}
                            {req.chat_transcript_summary && <div className="col-span-2"><span className="font-bold text-slate-500">Chat Summary:</span> {req.chat_transcript_summary}</div>}
                            {req.notes && <div className="col-span-2"><span className="font-bold text-slate-500">Notes:</span> {req.notes}</div>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
